import {css, StyleSheet} from "aphrodite";
import moment from "moment";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {containsWritingCoachContent} from "@khan/assignments-utils";
import type {Assignment} from "@khan/class-types";

import {getContentKindLabelMap} from "./utils.ts";

import ContentArticleSVG from "./icons/content-article.svg";
import ContentExerciseSVG from "./icons/content-exercise.svg";
import ContentVideoSVG from "./icons/content-video.svg";
import khanmigoActivityIcon from "./icons/khanmigo-activity-icon.svg";
import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";

const contentKindIconMap: Record<string, string> = {
    AIGuideActivity: khanmigoActivityIcon,
    Exercise: ContentExerciseSVG,
    Article: ContentArticleSVG,
    Video: ContentVideoSVG,
    TopicUnitTest: ContentExerciseSVG,
    TopicQuiz: ContentExerciseSVG,
    Scratchpad: ContentExerciseSVG,
    Challenge: ContentExerciseSVG,
    Interactive: ContentExerciseSVG,
    Project: ContentExerciseSVG,
    Talkthrough: ContentExerciseSVG,
    CourseChallenge: ContentExerciseSVG,
} as const;

// Writing Coach assignments have reports,
// but AIGuideActivity content kind does not in general.
// Thus, we only broadly show the report link for the following content kinds,
// and we inline the condition to show the report for Writing Coach content.
const ReportAvailableContentKinds = [
    "Exercise",
    "TopicQuiz",
    "TopicUnitTest",
    "CourseChallenge",
];

interface Props {
    assignment?: Assignment;
    isChildAssignment: boolean;
}

const AssignmentProgressHeaderCell = (
    props: Props,
): null | React.ReactElement<React.ComponentProps<"div">> => {
    const {assignment, isChildAssignment} = props;

    if (!assignment) {
        return null;
    }

    const {id, kind, translatedTitle} = assignment.contents[0];
    const contentKindIcon = contentKindIconMap[kind];
    const contentKindLabelMap = getContentKindLabelMap();
    const contentKindLabel = contentKindLabelMap[kind];
    const isWritingCoachAssignment = containsWritingCoachContent([
        `${kind}:${id}`,
    ]);
    const reportLink = `/teacher/report/assignment/${assignment.id}`;

    return (
        <div className={css(styles.assignmentHeaderCell)}>
            <div
                className={css(styles.assignmentHeaderCellIcon)}
                aria-label={`Content kind ${contentKindLabel}`}
                title={contentKindLabel}
            >
                <PhosphorIcon
                    icon={contentKindIcon}
                    size="medium"
                    color="currentColor"
                />
            </div>

            <div className={css(styles.assignmentHeaderCellWrapper)}>
                <div
                    className={css(styles.assignmentHeaderCellTitle)}
                    aria-label={
                        assignment.title ? assignment.title : translatedTitle
                    }
                    data-testid="assignment-progress-header-label"
                >
                    {ReportAvailableContentKinds.includes(kind) &&
                    !isChildAssignment ? (
                        <Link href={reportLink}>{translatedTitle}</Link>
                    ) : kind === "AIGuideActivity" && assignment.title ? (
                        isWritingCoachAssignment ? (
                            <Link
                                href={`/writing-coach/assignment/${assignment.id}`}
                                target="_blank"
                                endIcon={
                                    <PhosphorIcon icon={externalLinkIcon} />
                                }
                            >
                                {assignment.title}
                            </Link>
                        ) : (
                            assignment.title
                        )
                    ) : (
                        translatedTitle
                    )}
                </div>
                <div
                    className={css(styles.assignmentHeaderCellDueDate)}
                    data-testid="assignment-progress-header-due-date"
                >
                    {moment(assignment.dueDate).format("MMM D")}
                </div>
            </div>
        </div>
    );
};

const styles = StyleSheet.create({
    assignmentHeaderCell: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        fontSize: 10,
        padding: 8,
        paddingInlineEnd: 0,
    },
    assignmentHeaderCellIcon: {
        height: 24,
        width: "100%",
        color: color.offBlack64,
        paddingBlockEnd: 4,
    },
    assignmentHeaderCellWrapper: {
        display: "flex",
        flexDirection: "column",
    },
    assignmentHeaderCellTitle: {
        overflow: "hidden",
        lineHeight: "1.2",
        alignSelf: "flex-end",
    },
    assignmentHeaderCellDueDate: {
        width: "100%",
        paddingBlockStart: 4,
        color: color.offBlack64,
    },
});

export default AssignmentProgressHeaderCell;
